/**
 * Vague 1 · Phase C.4 · Infrastructure offline-first.
 *
 * IndexedDB (via la lib `idb`) pour :
 *  - `outbox` : opérations terrain en attente de sync (FIFO)
 *  - `cache`  : reads cachés (vacations du jour, main courante, etc.)
 *
 * On garde la base **mono-user-mono-app** : pas de scope par tenant car
 * un agent installé = une session utilisateur. Si l'agent se déconnecte
 * (logout), on vide la DB (cf. clearOfflineDb).
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type OutboxOpKind =
  | "pointage"
  | "main_courante"
  | "ronde_scan";

export type OutboxOpStatus = "pending" | "failed";

export interface OutboxOp {
  /** UUID local généré côté agent. */
  id: string;
  kind: OutboxOpKind;
  /** Body exact à POSTer (sérialisé prêt à l'envoi). */
  payload: unknown;
  /** Quand l'op a été créée par l'agent (instant device). */
  createdAt: string;
  /** Nombre de tentatives de sync (incrément à chaque flush). */
  attempts: number;
  status: OutboxOpStatus;
  /** Dernier message d'erreur (4xx non transitoire). */
  lastError?: string;
  /** Contexte UI (utile pour décorer les listes : vacation source, etc.). */
  meta?: {
    vacationId?: string;
  };
}

export interface CacheEntry {
  /** Clé canonique : `GET:/v1/...` */
  key: string;
  /** Payload renvoyé par l'API à ce moment-là. */
  data: unknown;
  /** Timestamp de la mise en cache. */
  fetchedAt: string;
}

interface VictorAgentDB extends DBSchema {
  outbox: {
    key: string;
    value: OutboxOp;
    indexes: {
      "by-status": OutboxOpStatus;
      "by-createdAt": string;
    };
  };
  cache: {
    key: string;
    value: CacheEntry;
  };
}

const DB_NAME = "victor-agent";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<VictorAgentDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<VictorAgentDB>> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(
      new Error("IndexedDB indisponible (environnement SSR ou navigateur trop ancien)."),
    );
  }
  if (!dbPromise) {
    dbPromise = openDB<VictorAgentDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("outbox")) {
          const outbox = db.createObjectStore("outbox", { keyPath: "id" });
          outbox.createIndex("by-status", "status");
          outbox.createIndex("by-createdAt", "createdAt");
        }
        if (!db.objectStoreNames.contains("cache")) {
          db.createObjectStore("cache", { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

/** Vidange complète · appelée au logout pour ne pas leaker entre comptes. */
export async function clearOfflineDb(): Promise<void> {
  try {
    const db = await getDb();
    await Promise.all([db.clear("outbox"), db.clear("cache")]);
  } catch {
    // Ignore : si la DB n'est pas dispo (Safari privé par ex.) on continue.
  }
}
